import { useCallback } from 'react';

import type { ArrivalSelection } from '@/features/arrival/types';
import type { Screen } from '@/lib/kioskSession';
import { useKioskSession } from '@/lib/kioskSession';
import { submitArrivalReason } from '@/services/arrivalReason';

/**
 * **The act of choosing why the candidate is here** — record the selection, route, post it [KID-5].
 *
 * Extracted from `ArrivalReasonScreen`'s row tap so a second place that makes the same choice makes it the same
 * way, with one post. That second place is the status screen's *Yes* to *"Would you like to apply for a new
 * job?"*, which is the menu's *Search for a job* row taken from somewhere else [03-us-kas-1a Scenario 11; 0511
 * point 9; 0492 point 1 — the entry's arrival reason follows the visit's latest purpose]. In `shared/` because it
 * has two consumers in two features (convention skill § 4).
 *
 * **Routes first and never waits on the network** [BR-K7; decision 0046]: the selection and the route happen
 * now, and the post is HELD, never awaited, so a hand-off raised on the destination is ordered after it by
 * `summonRepresentative` [KID-5d review round 1, M2].
 *
 * The caller passes the whole selection: `ArrivalReasonScreen` still writes the retired `queued` tombstone
 * (`../features/arrival/types.ts`, *"Nothing new may call it"*), and a new caller simply leaves it out.
 */
export function useSelectArrival(): (selection: ArrivalSelection, destination: Screen) => void {
  const candidateId = useKioskSession((state) => state.lookupResult?.candidateId);
  const setArrival = useKioskSession((state) => state.setArrival);
  const goTo = useKioskSession((state) => state.goTo);
  const holdArrivalPost = useKioskSession((state) => state.holdArrivalPost);

  return useCallback(
    (selection: ArrivalSelection, destination: Screen) => {
      setArrival(selection);
      goTo(destination);
      /*
       * Every selection is posted, including the one that does not queue. The rule that `reconfirm` raises
       * no entry belongs to the BACKEND — it is what writes entries, and an invariant enforced where the
       * write happens is one a second client cannot break [KID-5 § Actors].
       */
      holdArrivalPost(
        submitArrivalReason({
          reason: selection.reason,
          ...(candidateId === undefined ? {} : { candidateId }),
        }),
      );
    },
    [candidateId, goTo, holdArrivalPost, setArrival],
  );
}
